import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { ThumbsUp, MessageSquare, Reply, Circle, CheckCheck, Clock, Bell } from "lucide-react";

const NotificationFeed = ({ 
  userId, 
  onNotificationClick,
  setUnreadCount 
}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchNotifications();
    }
  }, [userId]);

  const fetchNotifications = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("notifications")
      .select(`
        *,
        actor:actor_id(campus_alias, avatar_url)
      `)
      .eq("recipient_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);

    if (!error) {
      setNotifications(data || []);
      // Reset unread count locally when the page is opened
      // Or we can mark selectively. For now, let's just fetch.
    }
    setLoading(false);
  };

  const markAsRead = async (notifId) => {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notifId);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("recipient_id", userId)
      .eq("is_read", false);

    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "like":
        return <ThumbsUp size={16} className="text-pink-500" />;
      case "comment":
        return <MessageSquare size={16} className="text-blue-500" />;
      case "reply":
        return <Reply size={16} className="text-indigo-500" />;
      default:
        return <Bell size={16} className="text-slate-400" />;
    }
  };

  const getMessage = (type) => {
    switch (type) {
      case "like":
        return "liked your post";
      case "comment":
        return "commented on your post";
      case "reply":
        return "replied to your comment";
      default:
        return "interacted with you";
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="max-w-2xl mx-auto pb-20">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Notifications
          </h2>
          <p className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">
            Recent Activity & Signals
          </p>
        </div>
        
        {notifications.some(n => !n.is_read) && (
          <button 
            onClick={markAllAsRead}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-all self-start sm:self-center"
          >
            <CheckCheck size={16} />
            Mark all read
          </button>
        )}
      </div>

      {/* Main List */}
      <div className="glass-card overflow-hidden dark:bg-white/5 dark:border-indigo-500/20 shadow-xl">
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-4 opacity-40">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-black uppercase tracking-[0.2em]">Synchronizing signals...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-24 text-center">
            <div className="w-20 h-20 bg-slate-50 dark:bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Bell size={32} className="text-slate-200 dark:text-slate-700" />
            </div>
            <h3 className="text-lg font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest">
              Zero Signal
            </h3>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-2 font-medium">
              We'll alert you when someone interacts with your posts.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-indigo-500/10">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => {
                  if (!notif.is_read) markAsRead(notif.id);
                  onNotificationClick(notif);
                }}
                className={`w-full flex items-start gap-4 p-5 text-left transition-all hover:bg-slate-50/50 dark:hover:bg-white/5 group relative ${
                  !notif.is_read ? "bg-indigo-50/30 dark:bg-indigo-500/5" : ""
                }`}
              >
                {/* Status Indicator Bar */}
                {!notif.is_read && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500" />
                )}

                <div className="relative shrink-0">
                  {notif.actor?.avatar_url ? (
                    <img 
                      src={notif.actor.avatar_url} 
                      className="w-12 h-12 rounded-2xl object-cover border border-slate-200 dark:border-indigo-500/30 shadow-sm" 
                      alt=""
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-sm border border-indigo-200 dark:border-indigo-400 shadow-sm">
                      {notif.actor?.campus_alias?.[0] || "?"}
                    </div>
                  )}
                  <div className="absolute -bottom-2 -right-2 bg-white dark:bg-slate-900 rounded-xl p-1.5 shadow-md border border-slate-100 dark:border-indigo-500/30">
                    {getIcon(notif.type)}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex justify-between items-start gap-2">
                    <p className="text-[14px] sm:text-[15px] text-slate-600 dark:text-slate-300 leading-snug">
                      <span className="font-black text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                        {notif.actor?.campus_alias || "Anonymous"}
                      </span>
                      {" "}{getMessage(notif.type)}
                    </p>
                    {!notif.is_read && (
                      <span className="shrink-0 w-2 h-2 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)] mt-1.5" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">
                      <Clock size={12} />
                      {getTimeAgo(notif.created_at)}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <span className="text-[10px] sm:text-[11px] text-indigo-500 dark:text-indigo-400 font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                      View Details
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center px-6">
        <p className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.3em]">
          End of history log • {notifications.length} interactions recorded
        </p>
      </div>
    </div>
  );
};

export default NotificationFeed;
